// Client for the yt-dlp download bridge on vps2 (the ONE server-side step — browser
// CORS forbids fetching YouTube bytes directly; see qlipper-web-project memory). The
// bridge fetches the video server-side and streams it back WITH CORS headers.
//
// Mock mode (localStorage 'qlipper-web:bridge' = 'mock') returns the bundled
// /sample.mp4 so the rest of the pipeline (transcribe → analyze → render) can be
// tested locally before the bridge is deployed / GATE A is confirmed.

const BRIDGE_BASE = import.meta.env?.VITE_BRIDGE_BASE || '/yt';

export function bridgeMode() {
  try { return localStorage.getItem('qlipper-web:bridge') || 'live'; } catch { return 'live'; }
}

async function mockDownload(onProgress) {
  for (let p = 10; p <= 100; p += 30) { onProgress?.(p); await new Promise((r) => setTimeout(r, 120)); }
  // optional override so a real-content test can swap in a downloaded clip
  let url = '/sample.mp4';
  try { url = localStorage.getItem('qlipper-web:mock-url') || url; } catch { /* ignore */ }
  const blob = await (await fetch(url)).blob();
  return { blob, title: 'Sample (mock bridge)', duration: 0, hasCaptions: false, vtt: null };
}

// Full download: start → poll progress → fetch bytes (+captions). Returns
// { blob, title, duration, hasCaptions, vtt }.
export async function downloadVideo({ url, format_id, preferredLang, cookieBrowser }, onProgress) {
  if (bridgeMode() === 'mock') return mockDownload(onProgress);

  let createRes;
  try {
    createRes = await fetch(`${BRIDGE_BASE}/jobs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, format_id, preferredLang, cookieBrowser }),
    });
  } catch (e) {
    const err = new Error('YouTube downloader is not reachable. The download bridge on the server is not running yet.');
    err.code = 'BRIDGE_DOWN';
    throw err;
  }
  // If /yt/ isn't wired, nginx serves the SPA's index.html (200 + text/html) instead
  // of the bridge JSON — detect that and give a clear message instead of a JSON parse crash.
  const ctype = createRes.headers.get('content-type') || '';
  if (!ctype.includes('application/json')) {
    const err = new Error('YouTube downloader is not set up on the server yet (the /yt/ bridge route is not active). Run bridge/deploy.sh, then try again.');
    err.code = 'BRIDGE_NOT_DEPLOYED';
    throw err;
  }
  if (!createRes.ok) throw new Error(`Bridge rejected download (${createRes.status})`);
  const { jobId } = await createRes.json();

  // poll progress
  const deadline = Date.now() + 30 * 60 * 1000;
  let meta = null;
  while (Date.now() < deadline) {
    const s = await (await fetch(`${BRIDGE_BASE}/jobs/${jobId}`)).json();
    if (typeof s.progress === 'number') onProgress?.(s.progress);
    if (s.status === 'completed') { meta = s; break; }
    if (s.status === 'failed') {
      const err = new Error(s.error || 'Download failed');
      err.code = s.errorCode;
      throw err;
    }
    await new Promise((r) => setTimeout(r, 1200));
  }
  if (!meta) throw new Error('Download timed out');

  const blob = await (await fetch(`${BRIDGE_BASE}/jobs/${jobId}/video`)).blob();
  let vtt = null;
  if (meta.hasCaptions) {
    try { const r = await fetch(`${BRIDGE_BASE}/jobs/${jobId}/captions`); if (r.ok) vtt = await r.text(); } catch { /* optional */ }
  }
  return { blob, title: meta.title || 'Video', duration: meta.duration || 0, hasCaptions: !!vtt, vtt };
}

// Probe formats/metadata (for the quality dropdown). Mock returns a small static set.
export async function probeMetadata(url) {
  if (bridgeMode() === 'mock') {
    return { title: 'Sample (mock)', duration: 5, hasCaptions: false,
      available_formats: [{ format_id: 'quality_720', height: 720, description: '720p' }] };
  }
  const r = await fetch(`${BRIDGE_BASE}/probe?url=${encodeURIComponent(url)}`);
  if (!r.ok) throw new Error(`Probe failed (${r.status})`);
  return r.json();
}
